import type { FeatureFlagMap } from "encamp-shared/src/featureFlags";

import AssignmentTurnedIn from "@mui/icons-material/AssignmentTurnedIn";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import { GridActionsCellItem, GridActionsCellItemProps } from "@mui/x-data-grid-premium";
import { documentEnrichmentIsEntitled } from "encamp-shared/src/documents";
import { ExtractionStatus, TierIiReportEncampStatus } from "generated-graphql/graphql";
import { ReactElement } from "react";
import { canGeneratePermitRenewalForDocument } from "util/permitRenewal";

import { Row } from ".";
import { readOnlyDocumentMessage } from "./documentWriteAccess";
import { buildLockableDocumentAction } from "./lockableDocumentAction";

type GetDocumentsGridRowActionsParams = {
  row: Row;
  canWriteDocument: boolean;
  canGeneratePermitRenewals: boolean;
  /** Resolved the way the retry mutation resolves them, staff overrides included. */
  featureFlags: FeatureFlagMap;
  retryLoading: boolean;
  generatePermitRenewalsLoading: boolean;
  openEditDialog: (row: Row) => void;
  fetchAndHandleDownload: (args: { documentId: string }) => Promise<void>;
  handleRetryDocumentExtractions: (row: Row) => Promise<void>;
  handleGeneratePermitRenewal: (row: Row) => Promise<void>;
  onRemoveDocument: (row: Row) => void;
};

export function getDocumentsGridRowActions({
  row,
  canWriteDocument,
  canGeneratePermitRenewals,
  featureFlags,
  retryLoading,
  generatePermitRenewalsLoading,
  openEditDialog,
  fetchAndHandleDownload,
  handleRetryDocumentExtractions,
  handleGeneratePermitRenewal,
  onRemoveDocument,
}: GetDocumentsGridRowActionsParams): ReactElement<GridActionsCellItemProps>[] {
  const actions: ReactElement<GridActionsCellItemProps>[] = [];
  const showGeneratePermitRenewal =
    canGeneratePermitRenewals && canGeneratePermitRenewalForDocument(row);
  const canModify = row.canWrite;
  const lockedMessage = readOnlyDocumentMessage(row);
  const showRetryExtraction =
    canWriteDocument &&
    documentEnrichmentIsEntitled(row.documentType, featureFlags, { explicitRequest: true });

  if (canWriteDocument) {
    actions.push(
      buildLockableDocumentAction({
        key: "edit-document",
        label: "Edit Document",
        Icon: Edit,
        canModify,
        lockedMessage,
        showInMenu: false,
        disabled: row.extractionStatus === ExtractionStatus.Pending,
        onClick: () => openEditDialog(row),
      })
    );
  }

  actions.push(
    <GridActionsCellItem
      key="download-document"
      onClick={async () =>
        await fetchAndHandleDownload({
          documentId: row.id,
        })
      }
      label="Download Document"
      icon={<Download />}
      disabled={row.extractionStatus === ExtractionStatus.Pending}
    />
  );

  if (showRetryExtraction) {
    actions.push(
      buildLockableDocumentAction({
        key: "retry-extraction",
        label: "Retry Extraction",
        Icon: Refresh,
        canModify,
        lockedMessage,
        showInMenu: true,
        disabled:
          row.extractionStatus === ExtractionStatus.Pending || retryLoading || !row.tenantId,
        onClick: () => void handleRetryDocumentExtractions(row),
      })
    );
  }

  if (showGeneratePermitRenewal) {
    actions.push(
      <GridActionsCellItem
        key="generate-permit-renewal"
        showInMenu={true}
        onClick={async () => {
          await handleGeneratePermitRenewal(row);
        }}
        label="Generate Permit Renewal"
        icon={<AssignmentTurnedIn />}
        disabled={generatePermitRenewalsLoading}
      />
    );
  }

  if (
    canWriteDocument &&
    (row.report == null || row.report.encampStatus !== TierIiReportEncampStatus.FilingComplete)
  ) {
    actions.push(
      buildLockableDocumentAction({
        key: "remove-document",
        label: "Remove Document",
        Icon: Delete,
        canModify,
        lockedMessage,
        showInMenu: showRetryExtraction || showGeneratePermitRenewal,
        disabled: row.extractionStatus === ExtractionStatus.Pending,
        onClick: () => onRemoveDocument(row),
      })
    );
  }

  return actions;
}
